import { addGuard } from "@/utils";
import { RPCRequests } from "@/modules/communication";
import { viewerStore } from "viewer/store/viewer";
import PresenceKeeper from "@/modules/app/Presence";

const { setCameraConfig } = viewerStore();

export default class Actions {
  private rpc!: RPC;
  private get jid() {
    return viewerStore().selectedCamera.jid as string;
  }

  init = (rpc: RPC) => {
    this.rpc = rpc;
  };

  changeCamera = async (cameraId: string) => {
    const request = RPCRequests.ChangeCamera.create({ cameraId });
    const rpcCall = await addGuard(() => this.rpc.call(request, this.jid) as Promise<CameraCommandResponse>, {
      onSuccess: (response) => {
        const { result, cameraAndFlashlightState } = response;
        if (result === "SUCCESS") setCameraConfig(this.jid, { cameraState: cameraAndFlashlightState });
      },
      onError: (err) => log.err("'changeLowLightMode' request failed", err)
    });
    return Boolean(rpcCall.result);
  };

  changeLowLightMode = async () => {
    const { lowLightMode } = viewerStore().cameraConfigs[this.jid].cameraState;
    const newLowLightState: LowLightMode = lowLightMode === "LL_AUTO" ? "LL_OFF" : "LL_AUTO";
    const request = RPCRequests.SetLowLightMode.create(newLowLightState);

    const rpcCall = await addGuard(() => this.rpc.call(request, this.jid) as Promise<CameraCommandResponse>, {
      onSuccess: (response) => {
        const { result, cameraAndFlashlightState } = response;
        if (result === "SUCCESS") setCameraConfig(this.jid, { cameraState: cameraAndFlashlightState });
      },
      onError: (err) => log.err("'changeLowLightMode' request failed", err)
    });
    return Boolean(rpcCall.result);
  };

  syncMonitoring = async () => {
    const request = RPCRequests.SyncMonitoring.create();
    const rpcCall = await addGuard(() => this.rpc.call(request, this.jid) as Promise<SyncMonitoringResponse>, {
      onSuccess: (response) => {
        const { batteryStatus, cameraState } = response;
        setCameraConfig(this.jid, { batteryStatus, cameraState });
      },
      onError: (err) => log.err("'syncMonitoring' request failed", err)
    });
    return Boolean(rpcCall.result);
  };

  changeCameraStationRecordingMode = async (mode: CameraStationRecordingMode, jid: string) => {
    return new Promise<boolean>((resolve) => {
      let timeout: NodeJS.Timeout;
      let receivedPresenceBeforeResponse = false;
      const request = RPCRequests.ChangeCameraStationRecordingMode.create(mode);
      const unsub = PresenceKeeper.on("presence-update", ({ jid: _jid, status }: { jid: string; status: Presence }) => {
        if (jid === _jid && (mode === "ON" ? status !== "sender_off" : status === "sender_off")) {
          log.info("'changeCameraStationRecordingMode' successful, presence update received", { jid, status });
          receivedPresenceBeforeResponse = true;
          unsub();
          clearTimeout(timeout);
          resolve(true);
        }
      });

      addGuard(() => this.rpc.call(request, jid) as Promise<{}>, {
        onSuccess: (response) => {
          log.info("'changeCameraStationRecordingMode' request successful, waiting for presence update...", response);
          if (receivedPresenceBeforeResponse) {
            log.info("'changeCameraStationRecordingMode' received presence before response, ignoring...");
            return;
          }
          timeout = setTimeout(() => {
            log.info("'changeCameraStationRecordingMode' presence update timeout", jid);
            unsub();
            resolve(false);
          }, 10000);
        },
        onError: (err) => {
          log.err("'changeCameraStationRecordingMode' request failed", err);
          unsub();
          resolve(false);
        }
      });
    });
  };

  setCameraAlarm = async () => {
    const currentState = viewerStore().cameraConfigs[this.jid].alarmStatus?.state;
    const newState = currentState === "ALARM_ON" ? "ALARM_OFF" : "ALARM_ON";

    const request = RPCRequests.SetCameraAlarm.create({ state: newState });
    const rpcCall = await addGuard(() => this.rpc.call(request, this.jid) as Promise<SetCameraAlarmResponse>, {
      onSuccess: (response) => {
        const { result } = response;
        if (result === "SUCCESS") setCameraConfig(this.jid, { alarmStatus: { state: newState } });
      },
      onError: (err) => log.err("'setAlarmStatus' request failed", err)
    });
    return Boolean(rpcCall.result);
  };

  getDetectionZonesPreview = async (jid: string) => {
    const request = RPCRequests.GetDetectionZonesPreview.create();
    const rpcCall = await addGuard(
      () => this.rpc.call(request, jid) as Promise<GetDetectionZonesPreviewResponse | false>,
      {
        onSuccess: (response) => log.info("'getDetectionZonesPreview' request successful", response),
        onError: (err) => log.err("'getDetectionZonesPreview' request failed", err)
      }
    );
    return rpcCall.result;
  };

  setCameraVideoRecording = async () => {
    const currentState = viewerStore().cameraConfigs[this.jid].videoRecordingStatus;
    const newState = currentState?.recordingOn ? "RECORDING_OFF" : "RECORDING_ON";

    const request = RPCRequests.SetCameraVideoRecording.create({ state: newState });
    const rpcCall = await addGuard(() => this.rpc.call(request, this.jid) as Promise<SetCameraVideoRecordingResponse>, {
      onSuccess: (response) => {
        const { result } = response;
        if (result === "SUCCESS") setCameraConfig(this.jid, { videoRecordingStatus: response.status });
      },
      onError: (err) => log.err("'setCameraVideoRecording' request failed", err)
    });
    return Boolean(rpcCall.result);
  };
}
